import React, { useState } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import apiClient from "../../api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../api/header";
import { PageHeader, PageHeaderHeading, PageHeaderActions } from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Search,
  RotateCcw,
  Package,
  Layers,
  History,
  MoreVertical,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../Router/all_routes";
import TransactionHistoryModal from "./components/TransactionHistoryModal";
import RecordNotFound from "@/core/components/RecordNotFound";

const InventoryList = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [storeId, setStoreId] = useState("");
  const [lowStock, setLowStock] = useState(null);

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Modal states
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    item: null,
  });

  // Queries
  const { data: inventoryData, isLoading } = useQuery({
    queryKey: [
      "inventory",
      currentPage,
      pageSize,
      searchText,
      storeId,
      lowStock,
    ],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.INVENTORY, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || null,
          store_id: storeId || null,
          low_stock:
            lowStock === "true" ? true : lowStock === "false" ? false : null,
          with: "product,store",
        },
      });
      return res.data;
    },
    enabled: hasPermission("inventory read"),
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
    enabled: hasPermission("store read"),
  });

  const inventory = inventoryData?.data || [];
  const paginationMeta = {
    current_page: inventoryData?.meta?.current_page || 1,
    last_page: inventoryData?.meta?.last_page || 1,
    per_page: pageSize,
    total: inventoryData?.meta?.total || 0,
  };

  const columns = [
    {
      accessorKey: "product.name",
      header: "Product",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.product?.name || "-"}</span>
      ),
    },
    {
      accessorKey: "sku",
      header: "SKU",
      cell: ({ row }) => (
        <span>{row.original.sku || row.original.product?.sku || "-"}</span>
      ),
    },
    {
      accessorKey: "store.name",
      header: "Store",
      cell: ({ row }) => <span>{row.original.store?.name || "-"}</span>,
    },
    {
      accessorKey: "product.tracking_type",
      header: "Tracking",
      cell: ({ row }) => {
        const type = row.original.product?.tracking_type || "none";
        const variants = {
          none: "secondary",
          standard: "outline",
          serial: "primary",
        };
        const labels = {
          none: "None",
          standard: "Batch",
          serial: "Serial",
        };

        if (!hasPermission("inventory read")) {
          return <RecordNotFound />;
        }
        return (
          <Badge variant={variants[type]} className="capitalize">
            {labels[type]}
          </Badge>
        );
      },
    },
    {
      accessorKey: "batches_count",
      header: "Batches",
      cell: ({ row }) => {
        const isTracked = row.original.product?.tracking_type === "standard";
        if (!isTracked) return <span className="text-muted-foreground">-</span>;
        if (!hasPermission("product update"))
          return (
            <span className="font-bold">{row.original.batches_count || 0}</span>
          );
        return (
          <Button
            variant="link"
            className="p-0 h-auto font-bold"
            onClick={() =>
              navigate(
                all_routes.editproduct.replace(":id", row.original.product?.id),
              )
            }
          >
            {row.original.batches_count || 0}
          </Button>
        );
      },
    },
    {
      accessorKey: "serials_count",
      header: "Serials",
      cell: ({ row }) => {
        const isTracked = row.original.product?.tracking_type === "serial";
        if (!isTracked) return <span className="text-muted-foreground">-</span>;
        if (!hasPermission("inventory serial track"))
          return (
            <span className="font-bold">{row.original.serials_count || 0}</span>
          );
        return (
          <Button
            variant="link"
            className="p-0 h-auto font-bold"
            onClick={() =>
              navigate(
                `${all_routes.serialtrack}?search=${row.original.product?.sku}`,
              )
            }
          >
            {row.original.serials_count || 0}
          </Button>
        );
      },
    },
    {
      accessorKey: "quantity",
      header: "Quantity",
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.quantity <= (row.original.reorder_level || 0)
              ? "destructive"
              : "outline"
          }
        >
          {row.original.quantity}
        </Badge>
      ),
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? "success" : "destructive"}>
          {row.original.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {hasPermission("product update") && (
              <DropdownMenuItem
                className="gap-2"
                onClick={() =>
                  navigate(
                    all_routes.editproduct.replace(
                      ":id",
                      row.original.product?.id,
                    ),
                  )
                }
              >
                <Layers size={14} /> View Batches
              </DropdownMenuItem>
            )}
            {hasPermission("inventory serial track") && (
              <DropdownMenuItem
                className="gap-2"
                onClick={() =>
                  navigate(
                    `${all_routes.serialtrack}?search=${row.original.product?.sku}`,
                  )
                }
              >
                <Package size={14} /> View Serials
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="gap-2"
              onClick={() =>
                setHistoryModal({ isOpen: true, item: row.original })
              }
            >
              <History size={14} /> View Transactions
            </DropdownMenuItem>
            {hasPermission("inventory update") && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 text-destructive">
                  <Package size={14} /> Stock Adjustment
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    setStoreId("");
    setLowStock(null);
    setCurrentPage(1);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Inventory"
          subtitle="Manage stock levels across stores"
        />
      </PageHeader>

      <FilterCard>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
          <FilterSearch
            placeholder="Search products..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="md:col-span-1"
          />

          <ReactSelect
            options={stores.map((s) => ({
              value: String(s.id),
              label: s.name,
            }))}
            value={stores
              .map((s) => ({ value: String(s.id), label: s.name }))
              .find((o) => o.value === storeId)}
            onChange={(opt) => setStoreId(opt ? opt.value : "")}
            placeholder="All Stores"
            isClearable
          />

          <ReactSelect
            options={[
              { value: "true", label: "Low Stock" },
              { value: "false", label: "Healthy Stock" },
            ]}
            value={[
              { value: "true", label: "Low Stock" },
              { value: "false", label: "Healthy Stock" },
            ].find((o) => o.value === lowStock)}
            onChange={(opt) => setLowStock(opt ? opt.value : null)}
            placeholder="Stock Status"
            isClearable
          />

          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw size={16} /> Reset
          </Button>
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={inventory}
          enableRowSelection={true}
          isLoading={isLoading}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize: pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            } else {
              setCurrentPage(updater.pageIndex + 1);
              setPageSize(updater.pageSize);
            }
          }}
          pageCount={paginationMeta.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Inventory: {paginationMeta.total}
            </span>
          }
        />
      </TableCard>

      <TransactionHistoryModal
        show={historyModal.isOpen}
        onHide={() => setHistoryModal({ isOpen: false, item: null })}
        inventoryItem={historyModal.item}
      />
    </PageContainer>
  );
};

export default InventoryList;
